import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useLanguage } from '../i18n';
import { trackEvent } from '../utils/analytics';
import {
  ProgressBar,
  Step1_PLZCheck,
  Step2_Project,
  Step3_Demand,
  Step4_Details,
  Step5_Results,
  Step6_Contact,
  INITIAL_FORM_DATA,
  INITIAL_WAITLIST_DATA,
  convertToDisplayCurrency,
  API
} from '../components/calculator';
import axios from 'axios';

// API Services
import { calculateCosts, submitLead, generatePDF, loadCalculationInfo } from '../services/api';

// Custom Hooks
import { usePLZCheck } from '../hooks/usePLZCheck';
import { useAddressValidation } from '../hooks/useAddressValidation';
import { useFileUpload } from '../hooks/useFileUpload';

const Calculator = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [calculationResult, setCalculationResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Analytics: fire calculator_start once, track step transitions
  const startTracked = useRef(false);
  const prevStep = useRef(1);

  useEffect(() => {
    if (!startTracked.current) {
      trackEvent('calculator_start');
      startTracked.current = true;
    }
  }, []);

  const STEP_NAMES = { 1: 'location', 2: 'building_data', 3: 'energy_demand', 4: 'details', 5: 'results', 6: 'contact' };

  // Info popup state
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [infoContent, setInfoContent] = useState('');

  // PDF ref
  const resultsRef = useRef(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Waitlist
  const [waitlistData, setWaitlistData] = useState(INITIAL_WAITLIST_DATA);

  // Tooltip states
  const [showInvestTooltip, setShowInvestTooltip] = useState(false);
  const [showSeparatusInvestTooltip, setShowSeparatusInvestTooltip] = useState(false);
  const [showKonvInvestTooltip, setShowKonvInvestTooltip] = useState(false);
  const [showLuftInvestTooltip, setShowLuftInvestTooltip] = useState(false);
  const [showTableInvestTooltip, setShowTableInvestTooltip] = useState(false);
  const [showTableRenewalTooltip, setShowTableRenewalTooltip] = useState(false);

  // Custom Hooks
  const {
    plzCheckStatus, plzCheckData, plzMultipleOptions,
    showWaitlistForm, setPlzCheckStatus, setPlzCheckData,
    setPlzMultipleOptions, setShowWaitlistForm,
  } = usePLZCheck(formData.plz);

  const {
    addressValidation, showAddressConfirm, validatedAddress,
    addressPredictions, showAddressPredictions, addressCoordinates,
    validateAddress, searchAddressPredictions, selectAddressPrediction,
    setShowAddressConfirm, setAddressValidation,
  } = useAddressValidation(formData, setFormData);

  const {
    files, isDragging,
    handleFileChange, removeFile,
    handleDragEnter, handleDragLeave, handleDragOver, handleDrop,
  } = useFileUpload();

  // Handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleWaitlistInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWaitlistData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const submitWaitlist = async () => {
    if (!waitlistData.name || !waitlistData.email) {
      toast.error(t('toastNameEmailRequired'));
      return;
    }

    if (!waitlistData.consent) {
      toast.error(t('toastConsentRequired'));
      return;
    }

    try {
      setLoading(true);

      const formDataToSend = new FormData();
      const leadData = {
        plz: formData.plz,
        country: plzCheckData?.country || 'unknown',
        name: waitlistData.name,
        email: waitlistData.email,
        status: 'waitlist',
        consent_version: '1.0'
      };

      formDataToSend.append('data', JSON.stringify(leadData));

      await axios.post(`${API}/leads`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t('waitlistSuccess'));

      setWaitlistData({ name: '', email: '', consent: false });
      setFormData(prev => ({ ...prev, plz: '' }));

      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      console.error('Waitlist submission error:', error);
      toast.error(t('toastWaitlistError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoadCalculationInfo = async () => {
    await loadCalculationInfo(setInfoContent, setShowInfoPopup);
  };

  const goToStep = (nextStep) => {
    if (nextStep > prevStep.current) {
      trackEvent('calculator_step', { step_number: nextStep, step_name: STEP_NAMES[nextStep] });
    }
    prevStep.current = nextStep;
    setCurrentStep(nextStep);
  };

  const proceedToStep2 = () => {
    if (plzCheckStatus === 'available' && plzCheckData) {
      setFormData(prev => ({ ...prev, country: plzCheckData.country, location_name: plzCheckData.location_name || '' }));
      goToStep(2);
    } else {
      toast.error(t('toastPlzRequired'));
    }
  };

  const handleMultipleOptionSelect = (option) => {
    setFormData(prev => ({ ...prev, country: option.country, location_name: option.location_name || '' }));
    setPlzCheckData(option);
    setPlzCheckStatus(option.available ? 'available' : 'unavailable');
    setPlzMultipleOptions([]);

    if (!option.available) {
      setShowWaitlistForm(true);
    } else {
      setShowWaitlistForm(false);
    }
  };

  const handleCalculateCosts = async () => {
    await calculateCosts(formData, setCalculationResult, (step) => { goToStep(step); }, setLoading);
  };

  const handleSubmitLead = async () => {
    await generatePDF(resultsRef, formData, setGeneratingPDF);
    await submitLead(formData, calculationResult, files, navigate, setLoading);
  };

  const convertToDisplayCurrencyWrapper = (amountEur) => {
    return convertToDisplayCurrency(amountEur, calculationResult);
  };

  return (
    <div className="calculator-container" data-testid="calculator-page">
      <div className="calculator-header">
        <img
          src="https://customer-assets.emergentagent.com/job_lead-wizard-5/artifacts/xohrqn39_Logo%20separatus%20vergleich.png"
          alt="separatus Vergleich"
          style={{ maxWidth: '300px', margin: '0 auto', display: 'block', cursor: 'pointer' }}
          onClick={() => navigate('/')}
          data-testid="calculator-logo"
        />
      </div>

      <div className="calculator-card">
        <ProgressBar currentStep={currentStep} />

        {currentStep === 1 && (
          <Step1_PLZCheck
            formData={formData}
            plzCheckStatus={plzCheckStatus}
            plzCheckData={plzCheckData}
            plzMultipleOptions={plzMultipleOptions}
            showWaitlistForm={showWaitlistForm}
            waitlistData={waitlistData}
            loading={loading}
            onInputChange={handleInputChange}
            onWaitlistInputChange={handleWaitlistInputChange}
            onMultipleOptionSelect={handleMultipleOptionSelect}
            onShowWaitlistForm={setShowWaitlistForm}
            onSubmitWaitlist={submitWaitlist}
            onProceedToStep2={proceedToStep2}
            onBackToHome={() => navigate('/')}
          />
        )}

        {currentStep === 2 && (
          <Step2_Project
            formData={formData}
            onInputChange={handleInputChange}
            onBack={() => setCurrentStep(1)}
            onNext={() => goToStep(3)}
          />
        )}

        {currentStep === 3 && (
          <Step3_Demand
            formData={formData}
            onInputChange={handleInputChange}
            onFormDataChange={setFormData}
            onBack={() => setCurrentStep(2)}
            onNext={() => goToStep(4)}
            onLoadInfo={handleLoadCalculationInfo}
          />
        )}

        {currentStep === 4 && (
          <Step4_Details
            formData={formData}
            onInputChange={handleInputChange}
            onFormDataChange={setFormData}
            onBack={() => setCurrentStep(3)}
            onCalculate={handleCalculateCosts}
            onLoadInfo={handleLoadCalculationInfo}
            loading={loading}
          />
        )}

        {(currentStep === 5 || currentStep === 6) && calculationResult && (
          <div style={currentStep === 6 ? { position: 'absolute', left: '-9999px', top: 0, width: '800px', pointerEvents: 'none' } : {}}>
            <Step5_Results
              calculationResult={calculationResult}
              resultsRef={resultsRef}
              onBack={() => setCurrentStep(4)}
              onNext={() => setCurrentStep(6)}
              onLoadInfo={handleLoadCalculationInfo}
              convertToDisplayCurrency={convertToDisplayCurrencyWrapper}
            />
          </div>
        )}

        {showInfoPopup && (
          <InfoPopupModal
            infoContent={infoContent}
            onClose={() => setShowInfoPopup(false)}
          />
        )}

        {currentStep === 6 && (
          <Step6_Contact
            formData={formData}
            files={files}
            isDragging={isDragging}
            showAddressPredictions={showAddressPredictions}
            addressPredictions={addressPredictions}
            showAddressConfirm={showAddressConfirm}
            validatedAddress={validatedAddress}
            addressValidation={addressValidation}
            addressCoordinates={addressCoordinates}
            loading={loading}
            onInputChange={handleInputChange}
            onFormDataChange={setFormData}
            onFileChange={handleFileChange}
            onRemoveFile={removeFile}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onSearchAddress={searchAddressPredictions}
            onSelectAddress={selectAddressPrediction}
            onValidateAddress={validateAddress}
            onCloseAddressConfirm={() => {
              setShowAddressConfirm(false);
              setAddressValidation(null);
            }}
            onBack={() => setCurrentStep(5)}
            onSubmit={handleSubmitLead}
          />
        )}
      </div>
    </div>
  );
};

// Extracted Info Popup Modal component
const InfoPopupModal = ({ infoContent, onClose }) => (
  <div
    style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'
    }}
    onClick={onClose}
  >
    <div
      style={{
        background: '#fff', borderRadius: '16px', width: '95%', maxWidth: '1000px',
        maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '1.5rem 2rem', borderBottom: '1px solid #e0e0e0', background: '#f8f9fa'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1B5E20', margin: 0 }}>
          Weitere Informationen
        </h2>
        <button
          onClick={onClose}
          style={{
            background: '#f0f0f0', border: 'none', fontSize: '1.5rem', cursor: 'pointer',
            color: '#666', width: '40px', height: '40px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = '#e0e0e0'}
          onMouseOut={(e) => e.target.style.background = '#f0f0f0'}
        >
          x
        </button>
      </div>

      <div style={{ overflow: 'auto', padding: '2rem', flex: 1 }}>
        <style>{`
          .info-popup-content { line-height: 1.8; color: #333; font-size: 1rem; }
          .info-popup-content h1 { font-size: 1.75rem; font-weight: 700; color: #1B5E20; margin: 1.5rem 0 1rem 0; border-bottom: 2px solid #4CAF50; padding-bottom: 0.5rem; }
          .info-popup-content h2 { font-size: 1.4rem; font-weight: 600; color: #2E7D32; margin: 1.5rem 0 0.75rem 0; }
          .info-popup-content h3 { font-size: 1.2rem; font-weight: 600; color: #388E3C; margin: 1.25rem 0 0.5rem 0; }
          .info-popup-content h4 { font-size: 1.1rem; font-weight: 600; color: #43A047; margin: 1rem 0 0.5rem 0; }
          .info-popup-content p { margin: 0.75rem 0; }
          .info-popup-content ul, .info-popup-content ol { margin: 0.75rem 0; padding-left: 1.5rem; }
          .info-popup-content li { margin: 0.4rem 0; }
          .info-popup-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 1rem 0; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
          .info-popup-content table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.95rem; }
          .info-popup-content table th { background: #4CAF50; color: white; padding: 0.75rem; text-align: left; font-weight: 600; }
          .info-popup-content table td { padding: 0.75rem; border-bottom: 1px solid #e0e0e0; }
          .info-popup-content table tr:hover { background: #f5f5f5; }
          .info-popup-content blockquote { border-left: 4px solid #4CAF50; margin: 1rem 0; padding: 0.75rem 1rem; background: #f8f9fa; font-style: italic; color: #555; }
          .info-popup-content a { color: #1B5E20; text-decoration: underline; }
          .info-popup-content a:hover { color: #4CAF50; }
          .info-popup-content strong, .info-popup-content b { font-weight: 600; color: #1B5E20; }
          .info-popup-content hr { border: none; border-top: 2px solid #e0e0e0; margin: 1.5rem 0; }
          .info-popup-content pre, .info-popup-content code { background: #f5f5f5; padding: 0.25rem 0.5rem; border-radius: 4px; font-family: monospace; font-size: 0.9rem; }
          .info-popup-content pre { padding: 1rem; overflow-x: auto; }
          .info-popup-content figure { margin: 1rem 0; text-align: center; }
          .info-popup-content figcaption { font-size: 0.85rem; color: #666; margin-top: 0.5rem; }
        `}</style>
        <div
          className="info-popup-content"
          dangerouslySetInnerHTML={{ __html: infoContent }}
        />
      </div>

      <div style={{
        padding: '1rem 2rem', borderTop: '1px solid #e0e0e0',
        background: '#f8f9fa', textAlign: 'right'
      }}>
        <button
          onClick={onClose}
          style={{
            background: '#4CAF50', color: 'white', border: 'none',
            padding: '0.75rem 2rem', borderRadius: '8px', fontSize: '1rem',
            fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = '#388E3C'}
          onMouseOut={(e) => e.target.style.background = '#4CAF50'}
        >
          Schliessen
        </button>
      </div>
    </div>
  </div>
);

export default Calculator;
